import React from 'react';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Select from '../../components/ui/Select/Select';
import Button from '../../components/ui/Button/Button';
import Modal from '../../components/ui/Modal/Modal';
import TimePicker from '../../components/ui/TimePicker/TimePicker';
import { useLayout } from '../../layout/LayoutContext';
import { useDeliveryForm, DeliveryFormState } from './useDeliveryForm';
import DeliveryFormSkeleton from './DeliveryFormSkeleton';
import styles from './DeliveryForm.module.css';

const ROLE_OPTIONS = [
  { label: 'Shipper', value: 'shipper' },
  { label: 'Consignee', value: 'consignee' },
  { label: 'Both', value: 'both' },
];

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode; full?: boolean }> = ({
  label,
  required,
  children,
  full,
}) => (
  <div className={[styles.field, full ? styles.full : ''].join(' ')}>
    <label className={styles.label}>
      {required && <span className={styles.req}>*</span>}
      {label}
    </label>
    {children}
  </div>
);

const CheckRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({
  label,
  checked,
  onChange,
  disabled,
}) => (
  <label className={styles.checkRow}>
    <input type="checkbox" className={styles.checkNative} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    <span className={[styles.checkBox, checked ? styles.checkOn : ''].join(' ')}>
      {checked && (
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
    {label}
  </label>
);

const DeliveryForm: React.FC = () => {
  const s = useDeliveryForm();
  const { form, perms } = s;
  const disabled = s.isEdit && !perms.canEdit;

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const [delOpen, setDelOpen] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const set = <K extends keyof DeliveryFormState>(k: K) => (e: React.ChangeEvent<HTMLInputElement>) =>
    s.setField(k, e.target.value as DeliveryFormState[K]);

  // Auto SCAC desde el nombre (iniciales) al escribir el nombre
  const onName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    s.setField('name', v);
    const scac = v.split(' ').filter(Boolean).map((w) => w[0]).join('').toUpperCase();
    s.setField('scac', scac);
  };

  const onCoordPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const m = e.clipboardData.getData('text').trim().match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (m) {
      e.preventDefault();
      s.setField('latitude', m[1]);
      s.setField('longitude', m[2]);
    }
  };

  const doSubmit = async () => {
    const ok = await s.submit();
    if (ok && s.isEdit) {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1600);
    }
  };

  if (s.loading) return <DeliveryFormSkeleton />;

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      {/* ===== Info ===== */}
      <Card className={s.submitting ? 'dylo-loading-host' : undefined}>
        {s.submitting && <span className="dylo-loading-sweep" />}
        <h2 className={styles.cardTitle}>{s.isEdit ? 'Delivery Detail' : 'Create Delivery'}</h2>

        <div className={styles.fieldsGrid}>
          <Field label="Full Name of the company" required>
            <Input value={form.name} disabled={disabled} onChange={onName} />
          </Field>
          <Field label="SCAC" required>
            <Input value={form.scac} disabled={disabled} onChange={set('scac')} />
          </Field>
          {perms.canViewClients && (
            <Field label="Client">
              <Select
                options={s.clients.map((c) => ({ label: c.name, value: c.id }))}
                value={form.client_id}
                placeholder="Select a client"
                allowClear
                disabled={disabled || !perms.canEditClients}
                onChange={(v) => s.setField('client_id', (v as string) || '')}
              />
            </Field>
          )}
          {/* Sin campo "User" desde el 13-oct-2026: ligaba la bodega a un usuario del rol Delivery, que se quitó
              (PENDIENTES_TERRESTRE, punto 9). El backend ya no lo guarda. */}
          <Field label="Role" required>
            <Select
              options={ROLE_OPTIONS}
              value={form.role}
              searchable={false}
              placeholder="Select a role"
              disabled={disabled}
              onChange={(v) => s.setField('role', (v as string) || '')}
            />
          </Field>
          <Field label="Commodity">
            <Select
              options={s.commodities.map((c) => ({ label: c.name, value: c.id }))}
              value={form.commodity_id}
              placeholder="Select a commodity"
              allowClear
              disabled={disabled}
              onChange={(v) => s.setField('commodity_id', (v as string) || '')}
              onAddItem={perms.canAddCommodity ? (name) => s.addCommodity(name) : undefined}
              addLabel="Add commodity"
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} disabled={disabled} onChange={set('phone')} />
          </Field>
          <Field label="Mail">
            <Input value={form.email} disabled={disabled} onChange={set('email')} />
          </Field>
          <Field label="Main Contact Person">
            <Input value={form.contact_main} disabled={disabled} onChange={set('contact_main')} />
          </Field>
          <Field label="Contact person at Warehouse">
            <Input value={form.contact_warehouse} disabled={disabled} onChange={set('contact_warehouse')} />
          </Field>

          <div className={styles.schedulesGroup}>
            <span className={styles.groupLabel}>Delivery schedules</span>
            <div className={styles.schedulesRow}>
              <Field label="Start Time">
                <TimePicker value={form.shedules_start} disabled={disabled} onChange={(v) => s.setField('shedules_start', v || '')} />
              </Field>
              <Field label="End Time">
                <TimePicker value={form.shedules_end} disabled={disabled} onChange={(v) => s.setField('shedules_end', v || '')} />
              </Field>
            </div>
          </div>
        </div>

        <div className={styles.checksGrid}>
          <CheckRow label="Is it stackable?" checked={form.stackable} disabled={disabled} onChange={(v) => s.setField('stackable', v)} />
          <CheckRow label="Is this a residential delivery place?" checked={form.redidential_place} disabled={disabled} onChange={(v) => s.setField('redidential_place', v)} />
          <CheckRow label="Is a lift or Dolly required for delivery?" checked={form.lift_dolly} disabled={disabled} onChange={(v) => s.setField('lift_dolly', v)} />
          <CheckRow label="Is open daily from Monday to Friday?" checked={form.open_daily} disabled={disabled} onChange={(v) => s.setField('open_daily', v)} />
          <CheckRow label="Is it mandatory to do an appointment?" checked={form.mandatory} disabled={disabled} onChange={(v) => s.setField('mandatory', v)} />
          <CheckRow label="Besides the POD, is a different document needed?" checked={form.beside_pod} disabled={disabled} onChange={(v) => s.setField('beside_pod', v)} />
        </div>
      </Card>

      {/* ===== Address ===== */}
      <Card>
        <h2 className={styles.cardTitle}>Delivery Address</h2>
        <div className={styles.grid4}>
          <Field label="Street 1" required>
            <Input value={form.street_one} disabled={disabled} onChange={set('street_one')} />
          </Field>
          <Field label="Street 2">
            <Input value={form.street_two} disabled={disabled} onChange={set('street_two')} />
          </Field>
          <Field label="Local Number">
            <Input value={form.local_number} disabled={disabled} onChange={set('local_number')} />
          </Field>
          <Field label="Colony">
            <Input value={form.colony} disabled={disabled} onChange={set('colony')} />
          </Field>
        </div>
        <div className={styles.grid3}>
          <Field label="Zip code" required>
            <Input value={form.zipcode} disabled={disabled} onChange={set('zipcode')} />
          </Field>
          <Field label="City" required>
            <Input value={form.city} disabled={disabled} onChange={set('city')} />
          </Field>
          <Field label="State" required>
            <Input value={form.state} disabled={disabled} onChange={set('state')} />
          </Field>
        </div>
        <div className={styles.grid2b}>
          <Field label="Latitude">
            <Input value={form.latitude} disabled={disabled} placeholder="e.g. 25.6866142" onChange={set('latitude')} onPaste={onCoordPaste} />
          </Field>
          <Field label="Longitude">
            <Input value={form.longitude} disabled={disabled} placeholder="e.g. -100.3161126" onChange={set('longitude')} onPaste={onCoordPaste} />
          </Field>
        </div>

        {s.error && <div className={styles.alert}>{s.error}</div>}
        {saved && <div className={styles.savedMsg}>Delivery updated successfully.</div>}

        {!disabled && (
          <div className={styles.actions}>
            <Button variant="primary" className={styles.submitBtn} fullWidth loading={s.submitting} disabled={s.requiredMissing} onClick={doSubmit}>
              {s.isEdit ? 'Update Delivery' : 'Create Delivery'}
            </Button>
            {s.isEdit && perms.canEdit && (
              <Button variant="ghost" className={styles.deleteBtn} loading={s.deleting} onClick={() => setDelOpen(true)}>
                Delete
              </Button>
            )}
          </div>
        )}
      </Card>

      <Modal
        open={delOpen}
        title="Delete Delivery"
        onClose={() => !s.deleting && setDelOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDelOpen(false)} disabled={s.deleting}>
              Cancel
            </Button>
            <Button variant="primary" className={styles.deleteConfirm} loading={s.deleting} onClick={s.remove}>
              Delete
            </Button>
          </>
        }
      >
        <p className={styles.confirmText}>
          Are you sure you want to delete <b>{form.name}</b>? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default DeliveryForm;
